/**
 * Auth modal — signup/login forms, Google OAuth, dev-only "Continue as admin".
 *
 * Two modes:
 *   - Default: dismissible (close button, Escape, click-outside, Cancel).
 *   - { strict: true }: non-dismissible. Only successful auth (form submit,
 *     Google, or admin) can close it. Used by the auth-first gate in shell.js
 *     before any builder UI mounts.
 *
 * Single-flight: only one modal can be open at a time. Concurrent callers
 * receive the same in-flight promise (prevents the auth gate from looping
 * after `if (overlay) return null` early-returns).
 *
 * Takes its shell from ui/components.js, NOT from shell.js. That distinction is
 * load-bearing and not stylistic: shell.js imports this module, so reaching the
 * modal primitives through it would re-create the evaluation-time cycle. The
 * kit's own import graph (modal-primitives, icons, sheet, viewport-service,
 * report-error) never touches shell.js, which is what makes this safe. The
 * modal renders inline feedback via setMessage — no toasts, so no other
 * shell.js dependency either.
 */

import { eventBus } from '../core/events.js';
import { loginAccount, signupAccount, checkAuth, startGoogleOAuth } from '../templates/auth-api.js';
import { TemplateApiError } from '../templates/template-api.js';
import { escapeHTML } from '../shared/html-escape.js';
import { createModal } from './components.js';
import { recordLastUsed, applyLastUsedBadge } from './login-last-used.js';

const PASSWORD_MIN = 8;

let _activeAuthPromise = null;

function isLegacyAdminAllowed() {
  return Boolean(globalThis.window?.__PB_ALLOW_LEGACY_ADMIN);
}

/**
 * Inline Google "G" logo. 18x18. Exported — the sign-in screen reuses it.
 */
export const GOOGLE_LOGO_SVG = `
<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
  <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
  <path d="M3.964 10.708A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.708V4.96H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.332z" fill="#FBBC05"/>
  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.29C4.672 5.164 6.656 3.58 9 3.58z" fill="#EA4335"/>
</svg>`.trim();

/**
 * Show the auth modal. Single-flight: concurrent callers receive the same promise.
 *
 * @param {object} [options]
 * @param {'login' | 'signup'} [options.mode='login']  Initial form
 * @param {string} [options.email='']                  Prefill email
 * @param {boolean} [options.strict=false]             If true, modal is non-dismissible
 * @param {(payload:object)=>void} [options.onSuccess] Callback after auth succeeds
 * @returns {Promise<{ok:boolean, sub?:string, email?:string} | null>} resolves when modal closes
 */
export function showAuthModal(options = {}) {
  if (_activeAuthPromise) return _activeAuthPromise;

  _activeAuthPromise = _openAuthModal(options);
  _activeAuthPromise.finally(() => {
    _activeAuthPromise = null;
  });
  return _activeAuthPromise;
}

function _openAuthModal(options) {
  return new Promise((resolve) => {
    const strict = options.strict === true;
    const initialMode = options.mode === 'signup' ? 'signup' : 'login';
    const adminVisible = isLegacyAdminAllowed();

    // Strict mode is the auth-first gate: nothing but a successful sign-in may
    // close it. Expressed through the kit's three dismissal switches rather
    // than by withholding four hand-wired listeners — and because
    // dismissOnBackdrop is false in strict mode, the kit's single auto-upgrade
    // condition also withholds drag-to-dismiss on a phone, which is exactly
    // right for a dialog the user is not allowed to dismiss.
    let outcome = null;
    const {
      overlay,
      header,
      titleEl,
      body,
      close: closeModal
    } = createModal({
      title: initialMode === 'signup' ? 'Create account' : 'Sign in',
      maxWidth: 440,
      className: 'pb-auth-modal',
      overlayClassName: 'pb-auth-overlay',
      withFooter: false,
      showClose: !strict,
      dismissOnBackdrop: !strict,
      initialFocus: '#pb-auth-email',
      onEscape: () => !strict,
      onClose: () => {
        if (!resolved) {
          resolved = true;
          resolve(outcome);
        }
      }
    });
    // No `pb-auth-header` hook: the header wears the kit's own padding and
    // hairline now, the same as the template picker's. It carried a tinted
    // toolbar bar; that rule is gone, so an empty class hook would only be a
    // selector waiting to be re-filled.
    if (strict) overlay.dataset.strict = 'true';

    // The kit puts the title straight in the header; this one sits next to a
    // brand mark, so it moves into a wrapper. Done by moving the kit's own
    // element rather than authoring a new <h2>, so aria-labelledby stays wired.
    const titleWrap = document.createElement('div');
    titleWrap.className = 'pb-auth-title-wrap';
    titleWrap.innerHTML =
      '<span class="pb-auth-icon" aria-hidden="true">' +
      '<img src="assets/brand/icon-256.png" alt="" width="18" height="18"></span>';
    header.insertBefore(titleWrap, header.firstChild);
    titleWrap.appendChild(titleEl);

    body.innerHTML = `
        <form class="pb-auth-form" id="pb-auth-form" data-mode="${initialMode}">
          <button type="button" class="pb-btn pb-auth-google" id="pb-auth-google" aria-label="Continue with Google">
            <span class="pb-auth-google-logo">${GOOGLE_LOGO_SVG}</span>
            <span>Continue with Google</span>
          </button>
          <div id="pb-auth-passkey-host"></div>
          <div class="pb-auth-divider"><span>or</span></div>
          <div class="pb-auth-toolbar">
            <button type="button" class="pb-auth-tab" data-mode="login">Sign in</button>
            <button type="button" class="pb-auth-tab" data-mode="signup">Create account</button>
          </div>
          <label class="pb-auth-field">
            <span>Email</span>
            <input class="pb-input" id="pb-auth-email" name="email" type="email" required autocomplete="email" placeholder="you@example.com" value="${escapeHTML(options.email || '')}" />
          </label>
          <label class="pb-auth-field">
            <span>Password</span>
            <input class="pb-input" id="pb-auth-password" name="password" type="password" required minlength="${PASSWORD_MIN}" autocomplete="current-password" placeholder="${PASSWORD_MIN}+ characters" />
          </label>
          <div class="pb-auth-message" id="pb-auth-message" aria-live="polite"></div>
          <div class="pb-auth-actions">
            ${strict ? '' : '<button type="button" class="pb-btn pb-btn-ghost" id="pb-auth-cancel">Cancel</button>'}
            <button type="submit" class="pb-btn pb-btn-primary" id="pb-auth-submit">${initialMode === 'signup' ? 'Create account' : 'Sign in'}</button>
          </div>
          ${
            adminVisible
              ? `
          <div class="pb-auth-divider"><span>or</span></div>
          <button type="button" class="pb-btn pb-btn-ghost pb-auth-admin" id="pb-auth-admin" data-tooltip="Skip auth in development">
            Continue as admin (dev only)
          </button>
          `
              : ''
          }
        </form>
    `;

    let resolved = false;
    const form = body.querySelector('#pb-auth-form');
    const emailInput = body.querySelector('#pb-auth-email');
    const passwordInput = body.querySelector('#pb-auth-password');
    const messageEl = body.querySelector('#pb-auth-message');
    const submitBtn = body.querySelector('#pb-auth-submit');
    const cancelBtn = body.querySelector('#pb-auth-cancel'); // null in strict mode
    const adminBtn = body.querySelector('#pb-auth-admin');
    const googleBtn = body.querySelector('#pb-auth-google');

    function close(result = null) {
      outcome = result;
      closeModal();
    }

    function setBusy(busy) {
      submitBtn.disabled = busy;
      emailInput.disabled = busy;
      passwordInput.disabled = busy;
      submitBtn.classList.toggle('pb-btn-loading', busy);
      if (adminBtn) adminBtn.disabled = busy;
      if (googleBtn) googleBtn.disabled = busy;
    }

    function setMessage(text, kind = 'info') {
      messageEl.textContent = text || '';
      messageEl.classList.toggle('pb-auth-message-error', kind === 'error');
      messageEl.classList.toggle('pb-auth-message-success', kind === 'success');
    }

    function setMode(mode) {
      const next = mode === 'signup' ? 'signup' : 'login';
      form.dataset.mode = next;
      submitBtn.textContent = next === 'signup' ? 'Create account' : 'Sign in';
      passwordInput.autocomplete = next === 'signup' ? 'new-password' : 'current-password';
      titleEl.textContent = next === 'signup' ? 'Create account' : 'Sign in';
      body.querySelectorAll('.pb-auth-tab').forEach((tab) => {
        const on = tab.dataset.mode === next;
        tab.classList.toggle('pb-auth-tab-active', on);
        // These are filled pills now, exactly like the template picker's
        // filters. A filled control that says nothing about its state is
        // colour alone; the picker's pills carry aria-pressed and so do these.
        tab.setAttribute('aria-pressed', String(on));
      });
      setMessage('');
    }

    setMode(initialMode);

    body.querySelectorAll('.pb-auth-tab').forEach((tab) => {
      tab.addEventListener('click', () => setMode(tab.dataset.mode));
    });

    // The close button, the backdrop and Escape are the kit's, gated by the
    // switches above. Only Cancel is this dialog's own control.
    if (cancelBtn) cancelBtn.addEventListener('click', () => close(null));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const mode = form.dataset.mode;
      const email = emailInput.value.trim().toLowerCase();
      const password = passwordInput.value;
      if (!email || !password) {
        setMessage('Email and password are required.', 'error');
        return;
      }
      if (password.length < PASSWORD_MIN) {
        setMessage(`Password must be at least ${PASSWORD_MIN} characters.`, 'error');
        return;
      }
      setBusy(true);
      setMessage(mode === 'signup' ? 'Creating account…' : 'Signing in…');
      try {
        const result =
          mode === 'signup'
            ? await signupAccount({ email, password })
            : await loginAccount({ email, password });
        setMessage(mode === 'signup' ? 'Account created.' : 'Signed in.', 'success');
        recordLastUsed('password');
        eventBus.emit('auth:changed', result);
        if (typeof options.onSuccess === 'function') options.onSuccess(result);
        resolved = true;
        resolve(result);
        // Brief delay so the success state is visible.
        setTimeout(() => close(result), 250);
      } catch (err) {
        const detail = err instanceof TemplateApiError ? err.message : 'Auth request failed';
        setMessage(detail, 'error');
        setBusy(false);
      }
    });

    if (googleBtn) {
      googleBtn.addEventListener('click', async () => {
        setBusy(true);
        setMessage('Opening Google sign-in…');
        let result;
        try {
          // startGoogleOAuth() calls window.open synchronously inside.
          result = await startGoogleOAuth();
        } catch (err) {
          const detail = err instanceof TemplateApiError ? err.message : 'Google sign-in failed';
          setMessage(detail, 'error');
          setBusy(false);
          return;
        }
        setMessage('Signed in.', 'success');
        recordLastUsed('google');
        eventBus.emit('auth:changed', result);
        if (typeof options.onSuccess === 'function') options.onSuccess(result);
        resolved = true;
        resolve(result);
        setTimeout(() => close(result), 100);
      });
    }

    // Passkey (mid-session re-auth parity with the sign-in screen): rendered
    // only when the backend supports passkeys on this origin. A passkey-primary
    // user whose session expires must not be stranded on password/Google.
    void (async () => {
      let availability = 'unavailable';
      try {
        const passkeyApi = await import('../templates/passkey-api.js');
        availability = await passkeyApi.getPasskeyAvailability();
        if (closed || availability !== 'available') return;
        const { runPasskeyCeremony } = await import('./passkey-ceremony.js');
        const host = body.querySelector('#pb-auth-passkey-host');
        if (!host) return;
        const wrap = document.createElement('div');
        wrap.dataset.loginMethod = 'passkey';
        wrap.innerHTML = `
          <button type="button" class="pb-btn pb-auth-google" id="pb-auth-passkey" aria-label="Sign in with a passkey">
            <span>Sign in with a passkey</span>
          </button>`;
        host.appendChild(wrap);
        applyLastUsedBadge(overlay);
        wrap.querySelector('#pb-auth-passkey').addEventListener('click', async () => {
          setBusy(true);
          setMessage('');
          try {
            const result = await runPasskeyCeremony(
              (signal) => passkeyApi.signInWithPasskey({ signal }),
              {
                title: 'Use your passkey',
                sub: 'Confirm with Touch ID, Face ID, or your device PIN.'
              }
            );
            setMessage('Signed in.', 'success');
            recordLastUsed('passkey');
            eventBus.emit('auth:changed', result);
            if (typeof options.onSuccess === 'function') options.onSuccess(result);
            resolved = true;
            resolve(result);
            setTimeout(() => close(result), 100);
            return;
          } catch (err) {
            if (err instanceof passkeyApi.PasskeyCancelledError) setMessage('');
            else setMessage(err?.message || 'Passkey sign-in failed.', 'error');
          }
          setBusy(false);
        });
      } catch {
        /* expected-flow fallback: passkey module/probe failure → button absent */
      }
    })();

    if (adminBtn) {
      adminBtn.addEventListener('click', async () => {
        setBusy(true);
        setMessage('Continuing as admin…');
        try {
          const result = await checkAuth();
          eventBus.emit('auth:changed', result);
          if (typeof options.onSuccess === 'function') options.onSuccess(result);
          resolved = true;
          resolve(result);
          close(result);
        } catch (err) {
          const detail = err instanceof TemplateApiError ? err.message : 'Auth check failed';
          setMessage(detail, 'error');
          setBusy(false);
        }
      });
    }
  });
}
